import { renderMetaTags, useQuerySubscription, TitleMetaLinkTag } from "react-datocms";
import classNames from "classnames";
import moment from "moment";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import { Layout, SEO, Section, Container, StickySideNav, HtmlToElement } from "@/components";
import { DATOCMS_API_TOKEN, PAGE_RECORDS } from "@/config";
import { metaTagsFragment } from "@/modules";
import { IChangelog, Product, ReleaseCategory, Subscription } from "@/types";
import { slugify } from "@/utils";
import { HorizontalRule } from "@narrative-software/narrative-web-ui";
import styles from "../../styles/custom.module.css";

const EMOJI_MAP: Record<ReleaseCategory["changeType"], string> = {
  New: "🐣",
  Improvements: "🏋️‍♀️",
  Fixes: "🔧",
};

interface IChangelogPage extends IChangelog {
  seo: TitleMetaLinkTag[];
}

type Props = {
  preview: boolean;
  subscription: Subscription<IChangelogPage | undefined>;
};

export const ChangelogPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const { title, subheading, releases, seo } = data;

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container>
          <div className="flex flex-col items-center text-center">
            <h1 className="text-h2 font-semibold leading-tight lg:text-h1">{title}</h1>
            <HorizontalRule topMargin bottomMargin />
            {subheading && <HtmlToElement className="max-w-200 lg:text-lg">{subheading}</HtmlToElement>}
          </div>
        </Container>
      </Section>

      <Section className="-mt-8 md:-mt-14 lg:-mt-18 xl:-mt-32">
        <Container>
          <div className={classNames("grid grid-cols-1 gap-15 lg:gap-22 xl:gap-30", styles.grid)}>
            <div>
              <StickySideNav
                title="Releases"
                items={releases.map(({ id, version, date }) => ({
                  id,
                  title: `v${version} - ${moment(date).format("D MMMM YYYY")}`,
                }))}
              />
            </div>
            <div>
              {releases.map(({ id, version, date, summary, items }, i) => {
                const title = `v${version} - ${moment(date).format("D MMMM YYYY")}`;
                return (
                  <article key={id} id={slugify(title)} className={classNames({ "pb-10": i < releases.length - 1 })}>
                    <h3 className="text-h3 font-semibold leading-tight mb-6">{title}</h3>
                    <HtmlToElement element="p" className="prose text-stealth-bomber leading-relaxed mb-7">
                      {summary}
                    </HtmlToElement>
                    {items.map(({ id: itemID, changeType, text }) => (
                      <article key={itemID} className="mb-7">
                        <h4 className="text-h4 font-semibold leading-tight mb-3">
                          {EMOJI_MAP[changeType]}&nbsp;&nbsp;{changeType}
                        </h4>
                        <HtmlToElement className="prose text-stealth-bomber leading-relaxed">{text}</HtmlToElement>
                      </article>
                    ))}
                  </article>
                );
              })}
            </div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: (product: Product) => GetStaticProps<Props> =
  (product) =>
  async ({ params, preview = false }) => {
    const graphqlRequest = {
      preview,
      variables: {
        slug: params?.slug,
      },
      query: `
      {
        pages: allPageChangelogs {
          title
          subheading
          slug
          parentPage {
            ... on ${PAGE_RECORDS.select} {
              slug
            }
            ... on ${PAGE_RECORDS.publish} {
              slug
            }
          }
          releases {
            id
            version
            date
            summary
            items {
              id
              changeType
              text
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
      }
      ${metaTagsFragment}
    `,
    };

    const {
      data: {
        data: { pages },
      },
    } = await Api.datocmsGraphqlRequest<{ pages: IChangelogPage[] }>(graphqlRequest);

    // Find the page that matches the product
    const initialData = pages.find(({ parentPage }) => parentPage.slug === product);
    initialData?.releases.reverse();

    return {
      props: {
        product,
        preview,
        subscription: preview
          ? {
              ...graphqlRequest,
              initialData,
              token: DATOCMS_API_TOKEN,
            }
          : {
              initialData,
              enabled: false,
            },
      },
    };
  };
